import {
  useCancelExport,
  useGetExportById,
  useGetExports,
} from "@apis/exports";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import {
  clearLatestExportData,
  setLatestExportData,
} from "@redux/slices/appStateSlice";
import { Export, ExportStatus } from "@src/types/ExportsResult";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useScreenOptionsHooks from "./useScreenOptionsHooks";
import Toast from "react-native-toast-message";
import { FileFormat } from "@components/molecules/Share/components/DownloadCSVModal";
import { useAppSettings } from "@redux/slices/globalSlice";

const useExportStatus = () => {
  const userToken = useSelector((state: any) => {
    if (!state || !state.userInfo || !state.userInfo.user) {
      return undefined;
    }
    return state.userInfo.user.token;
  });
  const exportEnabled = useSelector(
    (state: any) => state.globalData?.app_settings?.exports_enabled ?? false
  );
  const isLoggedIn = !!userToken && exportEnabled;

  const getExports = useGetExports(isLoggedIn && exportEnabled);
  const [exportFetched, setExportFetched] = useState(false);
  const [exportData, setExportData] = useState<Export>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dispatch = useDispatch();
  const { t, theme } = useScreenOptionsHooks();
  const latestExportData = useSelector(
    (state: any) => state?.appState?.latestExportData ?? undefined
  );
  const POLLING_INTERVAL =
    (useAppSettings()?.export_time_interval ?? 15) * 1000; // 15 seconds
  const cancelExportMutation = useCancelExport(exportData?.id as number);

  const getExportById = useGetExportById(latestExportData?.id as number);

  const clearPollingInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) return;

    clearPollingInterval();
    setExportFetched(false);
    setExportData(undefined);
    dispatch(clearLatestExportData());
    Toast.hide();
  }, [isLoggedIn, clearPollingInterval, dispatch]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (latestExportData?.id && latestExportData?.id !== exportData?.id) {
      setExportFetched(false);
      getExports.refetch();
    }
  }, [latestExportData?.id, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (getExports.isFetched && getExports.isSuccess && !exportFetched) {
      setExportFetched(true);
      const exports = getExports.data?.data?.exports ?? [];
      const activeExport = exports[0];
      setExportData(activeExport);
    }
  }, [getExports.isSuccess, getExports.isFetched, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (getExportById.isFetched && getExportById.isSuccess) {
      setExportData(getExportById.data?.data?.export);
      dispatch(setLatestExportData(getExportById.data?.data?.export));
    }
  }, [getExportById.data?.data?.export, isLoggedIn, dispatch]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!exportData?.id) return;

    if (
      exportData.status !== ExportStatus.in_progress &&
      exportData.status !== ExportStatus.hidden &&
      exportData.status !== ExportStatus.pending
    )
      return;

    if (intervalRef.current) return;

    // Initial fetch
    getExportById.refetch();

    intervalRef.current = setInterval(() => {
      getExportById.refetch();
    }, POLLING_INTERVAL);

    return () => {
      if (
        intervalRef.current &&
        exportData?.status !== ExportStatus.in_progress &&
        exportData?.status !== ExportStatus.hidden &&
        exportData?.status !== ExportStatus.pending
      ) {
        clearPollingInterval();
        setExportFetched(false);
        setExportData(undefined);
        dispatch(clearLatestExportData());
      }
    };
  }, [exportData?.id, isLoggedIn, POLLING_INTERVAL, getExportById]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const status = getExportById.data?.data?.export?.status;

    if (
      (status === ExportStatus.completed || status === ExportStatus.failed) &&
      intervalRef.current
    ) {
      clearPollingInterval();
    }
  }, [getExportById.data, getExportById.data?.data.export.status, isLoggedIn]);

  const clearExportData = useCallback(() => {
    setExportFetched(false);
    setExportData(undefined);
    dispatch(clearLatestExportData());
  }, [dispatch]);

  const cancelExport = useCallback(
    (status: ExportStatus) => {
      if (!cancelExportMutation.isPending) {
        cancelExportMutation.mutate(
          {
            status: status,
          },
          {
            onSuccess: (data) => {
              if (status !== ExportStatus.hidden) {
                clearExportData();
                clearPollingInterval();
              } else if (status === ExportStatus.hidden) {
                setExportData(data?.data?.export);
                dispatch(setLatestExportData(data?.data?.export));
              }
              setTimeout(() => {
                Toast.hide();
              }, 500);
            },
            onError: () => {
              setTimeout(() => {
                Toast.hide();
              }, 500);
            },
          }
        );
      }
    },
    [cancelExportMutation, clearExportData, clearPollingInterval, dispatch]
  );

  // Show toast only when status changes (avoid doing it during render/unmount).
  useEffect(() => {
    if (!isLoggedIn) return;
    const status = exportData?.status;
    if (!status || status === ExportStatus.hidden) return;

    if (status === ExportStatus.completed) {
      showToast(
        t("export.successTitle.text", "Done!"),
        t(
          "export.successDescription.text",
          "Export complete. Click here to download/save the file."
        ),
        ToastTypes.SUCCESS,
        true,
        false,
        theme.isLargeScreen ? 40 : 112,
        undefined,
        false,
        undefined,
        false,
        exportData?.progress,
        exportData?.file_url,
        cancelExport
      );
      return;
    }

    if (status === ExportStatus.in_progress) {
      showToast(
        exportData?.file_format === FileFormat.CSV
          ? t("export.toastTitle.CSV.textWithCount", "Preparing CSV file", {
              count: (
                exportData?.total_items ?? 0
              ).toLocaleString() as unknown as number,
            })
          : t("export.toastTitle.PDF.text", "Preparing PDF file"),
        t(
          "export.toastDescription.text",
          "Please don't close this window until the export is complete."
        ),
        ToastTypes.DOWNLOAD,
        true,
        true,
        theme.isLargeScreen ? 40 : 112,
        undefined,
        false,
        undefined,
        true,
        exportData?.progress,
        undefined,
        cancelExport
      );
      return;
    }

    if (status === ExportStatus.pending) {
      showToast(
        t("export.pendingTitle.text"),
        t("export.pendingDescription.text"),
        ToastTypes.INFO,
        true,
        true,
        theme.isLargeScreen ? 40 : 112,
        undefined,
        false,
        undefined,
        true,
        exportData?.progress,
        undefined,
        cancelExport
      );
    }
  }, [
    isLoggedIn,
    exportData?.status,
    exportData?.progress,
    exportData?.file_url,
    exportData?.file_format,
    theme.isLargeScreen,
    t,
    cancelExport,
  ]);
};

export default useExportStatus;
